import { html, nothing, type TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { renderExternalEmbedGate } from '../common/external-embed-gate.js';

export interface HtmlEmbedOptions {
  /** The document the iframe shows. */
  htmlSrc: string;
  /** The reader released the HTML for this view. */
  loaded: boolean;
  onLoad: () => void;
}

/**
 * Content of the HTML block: the external-content placeholder until the
 * reader loads the HTML, then the sandboxed iframe. The HTML may reference
 * images, scripts or fonts on other websites, so nothing of it renders
 * before the click.
 */
export function renderHtmlEmbed({
  htmlSrc,
  loaded,
  onLoad,
}: HtmlEmbedOptions): TemplateResult {
  if (!loaded) {
    return renderExternalEmbedGate({
      provider: 'html',
      previewImageUrl: null,
      onLoad,
    });
  }
  return html`<iframe
    class="embed-html-block-iframe"
    sandbox="allow-scripts"
    scrolling="no"
    .srcdoc=${htmlSrc}
    loading="lazy"
  ></iframe>`;
}

/**
 * Pointer shield over the loaded iframe, so a click selects the block instead
 * of landing in the HTML. Before the click there is no iframe to shield, and
 * the shield would cover the load button of the placeholder.
 */
export function renderHtmlEmbedOverlay({
  loaded,
  overlayHidden,
}: {
  loaded: boolean;
  overlayHidden: boolean;
}): TemplateResult | typeof nothing {
  if (!loaded) return nothing;
  return html`<div
    class=${classMap({
      'affine-embed-html-iframe-overlay': true,
      hide: overlayHidden,
    })}
  ></div>`;
}
